import { createFileRoute } from '@tanstack/react-router'
import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert } from '@/components/ui/alert';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

export default function DataImportPage() {
    const { t } = useI18n();
    const [selectedYear, setSelectedYear] = useState<string>('2025');
    const [selectedMonth, setSelectedMonth] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Generate years starting from 2025
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: Math.max(1, currentYear - 2024) }, (_, i) => 2025 + i);

    // Generate months
    const months = Array.from({ length: 12 }, (_, i) => ({
        value: (i + 1).toString(),
        label: t(`dataImport.monthNames.${i + 1}`)
    }));

    const handleImport = async () => {
        if (!selectedMonth) {
            setMessage({ type: 'error', text: 'Please select a month' });
            return;
        }

        setLoading(true);
        setMessage(null);

        try {
            // Calculate start date (first day of selected month)
            const startDate = new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1, 1);

            // Format as ISO strings for the API
            const startDateISO = startDate.toISOString();

            const response = await apiFetch(`/api/invoices/sync-nav-detailed?start_date=${encodeURIComponent(startDateISO)}`, {
                method: 'POST'
            });

            if (response.ok) {
                const result = await response.json();
                setMessage({
                    type: 'success',
                    text: `${t('dataImport.success')}. Found ${result.total_found || 0} invoices, imported ${result.imported || 0}, duplicates ${result.duplicates || 0}.`
                });
            } else {
                const errorData = await response.json();
                setMessage({
                    type: 'error',
                    text: errorData.detail || t('dataImport.error')
                });
            }
        } catch (error) {
            console.error('Import error:', error);
            setMessage({
                type: 'error',
                text: t('dataImport.error')
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {t('dataImport.title')}
                    </h1>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        {t('dataImport.description')}
                    </p>
                </div>

                <Card className="p-6">
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t('dataImport.selectYear')}
                                </label>
                                <Select value={selectedYear} onValueChange={setSelectedYear}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('dataImport.selectYear')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {years.map(year => (
                                            <SelectItem key={year} value={year.toString()}>
                                                {year}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t('dataImport.selectMonth')}
                                </label>
                                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('dataImport.selectMonth')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {months.map(month => (
                                            <SelectItem key={month.value} value={month.value}>
                                                {month.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {message && (
                            <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'}>
                                {message.text}
                            </Alert>
                        )}

                        <div className="flex justify-end">
                            <Button
                                onClick={handleImport}
                                disabled={loading || !selectedMonth}
                                className="flex items-center gap-2"
                            >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                                {loading ? t('dataImport.importing') : t('dataImport.importButton')}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/admin/data-import')({
  component: DataImportPage,
})
